import { cn } from '@dreamstack/util/src/cn'
import { normalizeHref } from '@dreamstack/util/src/normalizeHref'
import Link from 'next/link'
import type {
  ButtonHTMLAttributes,
  ComponentProps,
  MouseEvent,
  ReactElement,
} from 'react'
import { useState } from 'react'
import { DotAnimationLoader } from './DotAnimationLoader'

type Color = 'primary' | 'secondary' | 'warn'
type Size = 'xsmall' | 'small' | 'medium' | 'large'
type Variant = 'contained' | 'outlined' | 'text'

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  href?: string
  target?: '_blank' | '_self'
  color: Color
  size?: Size
  disabled?: boolean
  variant: Variant
  loading?: boolean
  loadingText?: string
  startIcon?: React.ReactNode
  endIcon?: React.ReactNode
  children?: string | ReactElement | ReactElement[] | null
}

const baseButtonClass =
  'font-semibold transition-all focus:outline-none overflow-hidden relative'

const paddingBySize: Record<Exclude<Size, 'medium'>, string> = {
  xsmall: 'pl-px-12 pr-px-12 pt-px-8 pb-px-8',
  small: 'pl-px-16 pr-px-16 pt-px-12 pb-px-12',
  large:
    'pl-px-24 pr-px-24 pt-px-16 pb-px-16 ml:pl-px-24 ml:pr-px-24 ml:pt-px-20 ml:pb-px-20',
}

const fontSizeBySize: Partial<Record<Size, string>> = {
  xsmall: 'text-sm',
  small: 'text-base',
  large: 'text-base ml:text-xl',
}

const containedBgByColor: Record<Color, string> = {
  primary: 'bg-accentroAqua-full',
  secondary: 'bg-accentroGray-full',
  warn: 'bg-accentroError-full',
}

const containedHoverBgByColor: Record<Color, string> = {
  primary: 'hover:bg-[#385964]',
  secondary: 'hover:bg-[#24262d]',
  warn: 'hover:bg-[#a8381b]',
}

const borderColorByColor: Record<Color, string> = {
  primary: 'border-accentroAqua-full',
  secondary: 'border-accentroGray-full',
  warn: 'border-accentroError-full',
}

const hoverBorderColorByColor: Record<Color, string> = {
  primary: 'hover:border-[#385964]',
  secondary: 'hover:border-[#24262d]',
  warn: 'hover:border-[#a8381b]',
}

const textColorByColor: Record<Color, string> = {
  primary: 'text-accentroAqua-full',
  secondary: 'text-accentroGray-full',
  warn: 'text-accentroError-full',
}

const rippleClass =
  'absolute h-3 w-3 bg-[#ffffffb3] rounded-full animate-ripple'

const useRipple = () => {
  const [ripple, setRipple] = useState(false)
  const [rippleLeft, setRippleLeft] = useState(0)
  const [rippleTop, setRippleTop] = useState(0)

  const rippleEvent = (e: MouseEvent<HTMLButtonElement>) => {
    const button = e.currentTarget
    const viewportOffset = button.getBoundingClientRect()
    const left = e.clientX - viewportOffset.left - 8
    const top = e.clientY - viewportOffset.top - 8
    setRippleLeft(left)
    setRippleTop(top)
    setTimeout(() => {
      setRipple(true)
    }, 0)
    setTimeout(() => {
      setRipple(false)
    }, 300)
  }

  const rippleComponent = ripple ? (
    <div
      className={rippleClass}
      style={{ left: `${rippleLeft}px`, top: `${rippleTop}px` }}
    />
  ) : null

  return {
    Ripple: rippleComponent,
    rippleEvent,
  }
}

/**
 * Use within `<Button>` or another div with className "group",
 * e.g. as `endIcon` to apply a bouncing animation on hover.
 */
export const AnimateBounceRight = ({
  className,
  ...rest
}: ComponentProps<'div'>) => (
  <div className={cn('group-hover:animate-bounceright', className)} {...rest} />
)

/**
 * Use within `<Button>` or another div with className "group",
 * e.g. as `startIcon` to apply a bouncing animation on hover.
 */
export const AnimateBounceLeft = ({
  className,
  ...rest
}: ComponentProps<'div'>) => (
  <div className={cn('group-hover:animate-bounceleft', className)} {...rest} />
)

const getPaddingClass = (variant: Variant, size: Size = 'large') => {
  if (variant === 'text') return undefined
  if (size === 'medium') {
    return paddingBySize.large
  }
  return paddingBySize[size]
}

const getFontSizeClass = (size: Size = 'large') => {
  if (size === 'medium') return undefined
  return fontSizeBySize[size]
}

const getBorderClasses = (
  variant: Variant,
  color: Color,
  disabled?: boolean
) => {
  if (variant === 'text') return undefined
  if (disabled) {
    return 'border-2 border-transparent hover:border-transparent'
  }
  return cn(
    'border-2',
    borderColorByColor[color],
    variant === 'contained' && hoverBorderColorByColor[color]
  )
}

const getBackgroundClasses = (
  variant: Variant,
  color: Color,
  disabled?: boolean
) => {
  if (disabled) {
    return variant === 'contained' ? 'bg-accentroGray-300' : undefined
  }

  if (variant === 'text') return 'bg-opacity-0'
  if (variant === 'outlined') return 'bg-opacity-0 hover:bg-opacity-[.15]'
  if (variant === 'contained') {
    return cn(containedBgByColor[color], containedHoverBgByColor[color])
  }
  return undefined
}

const getTextColorClasses = (
  variant: Variant,
  color: Color,
  disabled?: boolean
) => {
  if (disabled) {
    return variant === 'contained' ? 'text-white' : 'text-accentroGray-300'
  }
  if (variant === 'contained') return 'text-white'
  return textColorByColor[color]
}

export const Button = ({
  href,
  target,
  color,
  size = 'large',
  disabled,
  variant,
  loading,
  loadingText,
  startIcon,
  endIcon,
  children,
  className,
  onClick,
  ...rest
}: Props) => {
  const { rippleEvent, Ripple } = useRipple()

  const buttonClasses = cn(
    baseButtonClass,
    getPaddingClass(variant, size),
    getFontSizeClass(size),
    getBorderClasses(variant, color, disabled),
    getBackgroundClasses(variant, color, disabled),
    getTextColorClasses(variant, color, disabled),
    'group'
  )

  const contentClasses = 'flex items-center'
  const iconStartClasses = 'mr-px-16 ml:mr-px-20 -ml-px-4'
  const iconEndClasses = 'ml-px-16 ml:ml-px-20 -mr-px-4'

  const handleClick = (e: MouseEvent<HTMLButtonElement>) => {
    if (variant !== 'text') rippleEvent(e)
    onClick?.(e)
  }

  const buttonElement = (
    <button
      {...rest}
      className={cn(buttonClasses, className)}
      disabled={disabled}
      onClick={handleClick}
    >
      <div className={contentClasses}>
        {!!startIcon && <div className={iconStartClasses}>{startIcon}</div>}
        {!!loading ? (
          <DotAnimationLoader>
            {!!loadingText ? loadingText : children}
          </DotAnimationLoader>
        ) : (
          children
        )}
        {!!endIcon && <div className={iconEndClasses}>{endIcon}</div>}
      </div>
      {Ripple}
    </button>
  )

  if (href && !disabled) {
    return (
      <Link href={normalizeHref({ href })} passHref target={target ?? '_self'}>
        {buttonElement}
      </Link>
    )
  }

  return buttonElement
}
